import { useState } from "react"
import { DateRangePicker } from "../../shared/dateRangePicker"
import { SearchSelect } from "../../shared/searchSelect"
import { SearchFiltersModal } from "../../shared/searchFiltersModal"
import stringHelpers from "../../../utilities/stringHelpers"
import { reportDatePresetByLabel } from "../reportsEnums"
import { toDateString, dateRange, isDateRangeComplete } from "../../../utilities/dateUtilities"
import Api from "../../api/api"
import { TagSelector } from "../../shared/tagSelector"
import appliedFiltersUtilities from "../../../utilities/appliedFiltersUtilities"
import arrayUtilities from "../../../utilities/arrayUtilities"

const pcnsGroupByOptions = { 0: "Site and Amount", 1: "Site", 2: "Tag", 3: "Tag and Site" }
export const PcnsPaidReportFiltersModal = ({
    showModal,
    handleClose,
    handleConfirm,
    initialFromDate,
    initialToDate,
    initialDatePreset,
    initialIssueDateFrom,
    initialIssueDateTo,
    initialSelectedClient,
    initialSelectedSiteByClient,
    initialSitesByClient,
    initialPcnsGroupBy,
    initialShowZeroRevenueSites,
    initialSelectedTags = [],
    zeroRevenueSitesPreferenceKey,
    initialAppliedFilters,
    clients = [],
    sites = [],
    tags = [],
    organisationId,
    token
}) => {
    const [fromDate, setFromDate] = useState(initialFromDate)
    const [toDate, setToDate] = useState(initialToDate)
    const [datePreset, setDatePreset] = useState(initialDatePreset)
    const [issueDateFrom, setIssueDateFrom] = useState(initialIssueDateFrom)
    const [issueDateTo, setIssueDateTo] = useState(initialIssueDateTo)
    const [selectedClient, setSelectedClient] = useState(initialSelectedClient)
    const [selectedSiteByClient, setSelectedSiteByClient] = useState(initialSelectedSiteByClient)
    const [sitesByClient, setSitesByClient] = useState(initialSitesByClient)
    const [pcnsGroupBy, setPcnsGroupBy] = useState(initialPcnsGroupBy)
    const [showZeroRevenueSites, setShowZeroRevenueSites] = useState(initialShowZeroRevenueSites)
    const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
    const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

    const handleTagsChange = tagSelections => {
        setSelectedTags(tagSelections)
        setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
    }

    const isDateRangeInvalid = !isDateRangeComplete(fromDate, toDate) && !isDateRangeComplete(issueDateFrom, issueDateTo)

    const clearLocalFilters = () => {
        const defaultRange = dateRange("lastMonth")
        setFromDate(defaultRange.firstDate)
        setToDate(defaultRange.lastDate)
        setDatePreset(reportDatePresetByLabel["Last month"])
        setIssueDateFrom(null)
        setIssueDateTo(null)
        setSelectedClient("")
        setSelectedSiteByClient("")
        setSitesByClient(sites)
        setPcnsGroupBy(0)
        setSelectedTags([])
        const zeroRevenueSitesPreference = stringHelpers.zeroRevenueSitesPreference(zeroRevenueSitesPreferenceKey)
        setShowZeroRevenueSites(zeroRevenueSitesPreference)
        setAppliedFilters({
            "Payment Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`,
            "Group By": pcnsGroupByOptions[0],
            ...(zeroRevenueSitesPreference && { "Zero-Revenue Sites": "Included" })
        })
    }

    return (
        <SearchFiltersModal
            title="Filter Results"
            handleClose={ handleClose }
            handleConfirm={ () => handleConfirm({ fromDate, toDate, datePreset, issueDateFrom, issueDateTo, selectedClient, sitesByClient, selectedSiteByClient, pcnsGroupBy, showZeroRevenueSites, selectedTags, appliedFilters }) }
            showModal={ showModal }
            key="pcns-paid-report-filters-modal"
            handleReset={ clearLocalFilters }
            disableConfirm={ isDateRangeInvalid }
        >
            <div className="w-100">
                <div className="mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Payment Date Range</label>
                    <DateRangePicker
                        fromDate={ fromDate }
                        toDate={ toDate }
                        setDateRange={ (firstDate, lastDate, presetLabel) => {
                            setFromDate(firstDate === null || firstDate === "" ? null : firstDate);
                            setToDate(lastDate === null || lastDate === "" ? null : lastDate);
                            setDatePreset(stringHelpers.isNullOrEmpty(presetLabel) ? null : (reportDatePresetByLabel[presetLabel] ?? null))
                            setAppliedFilters(prevState => appliedFiltersUtilities.applyDateRange(prevState, "Payment Date", firstDate, lastDate, presetLabel))
                        } }
                        showClearOption
                        hideLabel
                        fullWidth
                        dropdownPositionRight
                    />
                </div>

                <div className="mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Issue Date Range</label>
                    <DateRangePicker
                        fromDate={ issueDateFrom }
                        toDate={ issueDateTo }
                        setDateRange={ (firstDate, lastDate, presetLabel) => {
                            setIssueDateFrom(firstDate === null || firstDate === "" ? null : firstDate)
                            setIssueDateTo(lastDate === null || lastDate === "" ? null : lastDate)
                            setAppliedFilters(prevState => appliedFiltersUtilities.applyDateRange(prevState, "Issue Date", firstDate, lastDate, presetLabel))
                        } }
                        showClearOption
                        hideLabel
                        fullWidth
                        dropdownPositionRight
                    />
                    { isDateRangeInvalid && <p className="text-danger fs--1 m-0 mt-1">Select a payment date range or issue date range</p> }
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Client</label>
                    <SearchSelect
                        defaultLabel="All"
                        width="100%"
                        options={ [{ value: "", label: "All" }, ...clients.map(item => ({ value: item.id, label: item.name }))] }
                        onSelect={ value => {
                            setSelectedClient(value)
                            if (stringHelpers.isNullOrEmpty(value)) {
                                setSitesByClient(sites)
                                setSelectedSiteByClient("")
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters["Client"]
                                    delete updatedFilters["Site"]
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            }
                            else {
                                Api.getClientSites(
                                    data => {
                                        setSitesByClient(data)
                                        setSelectedSiteByClient("")
                                    },
                                    error => console.log(error),
                                    value,
                                    organisationId,
                                    token
                                )
                                const selectedClientItem = clients.find(item => item.id === value)
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState, "Client": selectedClientItem?.name }
                                    delete updatedFilters["Site"]
                                    return updatedFilters
                                })
                            }
                        } }
                        selected={ selectedClient }
                    />
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Site</label>
                    <SearchSelect
                        defaultLabel="All"
                        width="100%"
                        options={ [{ value: "", label: "All" }, ...sitesByClient.map(item => ({ value: item.id, label: item.name }))] }
                        onSelect={ value => {
                            setSelectedSiteByClient(value)
                            if (stringHelpers.isNullOrEmpty(value)) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters["Site"]
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            } else {
                                const selectedSiteItem = sitesByClient.find(item => item.id === value)
                                setAppliedFilters(prevState => ({ ...prevState, "Site": selectedSiteItem?.name }))
                            }
                        } }
                        selected={ selectedSiteByClient }
                    />
                </div>

                { !arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
                    <div className="d-flex align-items-center justify-content-between">
                        <label className="m-0 mr-2 mb-1 nowrap">Tags</label>
                        <button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
                    </div>
                    <TagSelector
                        onChange={ handleTagsChange }
                        selectedTags={ selectedTags }
                        tags={ tags }
                    />
                </div> }

                <div className="d-flex flex-column mb-2">
                    <label htmlFor="pcns-group-by-selector" className="m-0 mr-2 mb-1 nowrap">Group By</label>
                    <select
                        id="pcns-group-by-selector"
                        className="border rounded px-2 py-1 w-100"
                        value={ String(pcnsGroupBy) }
                        onChange={ e => {
                            const value = Number(e.target.value)
                            setPcnsGroupBy(value)
                            setAppliedFilters(prevState => ({
                                ...prevState,
                                "Group By": pcnsGroupByOptions[value]
                            }))
                        } }
                    >
                        <option value="0">Site and Amount</option>
                        <option value="1">Site</option>
                        <option value="2">Tag</option>
                        <option value="3">Tag and Site</option>
                    </select>
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Include Zero-Revenue Sites</label>
                    <div className="custom-control custom-switch">
                        <input className="custom-control-input" type="checkbox" id="zero-revenue-toggle" checked={ showZeroRevenueSites }
                            onChange={ e => {
                                setShowZeroRevenueSites(e.target.checked)
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    if (!e.target.checked) {
                                        delete updatedFilters["Zero-Revenue Sites"]
                                    } else {
                                        updatedFilters["Zero-Revenue Sites"] = "Included"
                                    }
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            } }
                        />
                        <label className="custom-control-label custom-toggle" htmlFor="zero-revenue-toggle"></label>
                    </div>
                </div>
            </div>
        </SearchFiltersModal>
    )
}
